import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, Space, Popover, Typography, Checkbox, Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { accountActions } from '../../redux/actions';
import AccountStructureMenu from '../AccountStructureMenu';
import FormulaFunctions from './FormulaFunctions';
import Localization from '../../utils/localization';
import {
    validateFormula,
    checkAccountNumberExistance,
} from '../../utils/accountStructure/accountStructureUtils';
import './styles.scss';

// Always shown at the top of the account tree.
const OPERATORS = [
    { text: ' + ', label: '+' },
    { text: ' - ', label: '−' },
    { text: ' * ', label: '×' },
    { text: ' / ', label: '÷' },
    { text: '(', label: '(' },
    { text: ')', label: ')' },
];

const AccountEditing = ({ account, period, allAccounts, accountStructure, show, handleClose }) => {
    const dispatch = useDispatch();
    const accountNumber =
    account?.account == null ? account?.account_number : account?.account.account_number;
    const accountName =
    account?.account == null ? account?.name : account?.account.name;
    const updatingAccount = useSelector((state) => state.accounts.updatingAccount);
    const updatingFormulas = useSelector((state) => state.accounts.updatingFormulas);
    const updatingCustomTabAccount = useSelector(
        (state) => state.accounts.updatingCustomTabAccount,
    );

    const [formula, setFormula] = useState('');
    const [addFormulaAllPeriods, setAddFormulaAllPeriods] = useState(false);
    const [formulaError, setFormulaError] = useState('');
    const [formulaSelectingRelatedAccount, setFormulaSelectingRelatedAccount] = useState(false);
    const [accountsNotFound, setAccountsNotFound] = useState([]);

    // Account number just inserted from the tree; shows the operator bar until used.
    const [lastInsertedAccount, setLastInsertedAccount] = useState(null);
    // True right after a function template is inserted, so the next tree pick
    // fills its argument instead of offering operators.
    const fillingFunctionArgRef = useRef(false);

    // Track caret position in the formula input so we can splice tokens in at
    // the cursor instead of always appending to the end.
    const formulaInputRef = useRef(null);
    const caretPosRef = useRef(null);
    const formulaWrapperRef = useRef(null);
    const treeRef = useRef(null);

    // Applying a formula to later periods must be opted into on every edit.
    useEffect(() => {
        if (show) setAddFormulaAllPeriods(false);
    }, [show]);

    useEffect(() => {
        if (!account) return;

        const isNotPeriod = period === 'account_number' || period === 'name';
        if (isNotPeriod) return;

        if (account.formulas || account.account.formulas) {
            let index = 0;
            let formulas =
                account.account === undefined ? account.formulas : account.account.formulas;

            formulas.forEach((formula, newIndex) => {
                if (formula.period === period.slice(6)) {
                    index = newIndex;
                }
            });

            setFormula(formulas[index].formula);
        }
    }, [account, period, accountStructure]);

    const handleOk = async (e) => {
        let index = 0;
        let formulas = account.account === undefined ? account.formulas : account.account.formulas;

        formulas.forEach((formula, newIndex) => {
            if (formula.period === period.slice(6)) {
                index = newIndex;
            }
        });

        let shouldConfirm = true;

        let modifiedAccount =
            account.account === undefined
                ? { ...account }
                : {
                        ...account.account,
                        custom_tab_accounts_id: account.custom_tab_accounts_id,
                    };

                    const withFormulaAt = (formulas, i, value) =>
                        Array.isArray(formulas)
                            ? formulas.map((f, fi) => (fi === i ? { ...f, formula: value } : f))
                            : formulas;
                            
                            // An empty formula clears it, so the scenario uses the entered values.
                            const result = validateFormula(formula, accountStructure);

                            if (account.children !== undefined) {
                                shouldConfirm = true;
                            } else if (result.status !== 0 && formula !== '') {
                                shouldConfirm = false;
                                setFormulaError(result.error);
                            } else {
                                modifiedAccount.formulas = withFormulaAt(modifiedAccount.formulas, index, formula);
                                setFormulaError('');
                            }

                            if (shouldConfirm) {
                                const isCustomAccount = modifiedAccount.account !== undefined;
                                if (!updatingFormulas) {
                                    await dispatch(
                                        accountActions.updateFormulas(
                                            modifiedAccount,
                                            period,
                                            formula,
                                            isCustomAccount,
                                            addFormulaAllPeriods,
                                        ),
                                    );
                                }
                            }

                            if (shouldConfirm && modifiedAccount.custom_tab_accounts_id === undefined) {
                                if (!updatingAccount) {
                                    await dispatch(accountActions.updateAccount(account, modifiedAccount));
                                }
                            } else if (shouldConfirm) {
                                let updatedCustomAccount = { ...account };
                                updatedCustomAccount.account = modifiedAccount;

                                if (!updatingCustomTabAccount)
                                    await dispatch(accountActions.updateCustomTabAccount(updatedCustomAccount));
                            }
                            handleClose();
                        };

   

    const handleCancel = () => {
        handleClose();
    };

    const onFormulaChange = (e) => {
        // The box wraps visually, but formulas are stored on a single line.
        const currentFormula = e.target.value.replace(/\r?\n/g, ' ');
        setAccountsNotFound(checkAccountNumberExistance(currentFormula, allAccounts));
        setFormula(currentFormula);
        // Typing takes over from the operator bar / pending function argument.
        setLastInsertedAccount(null);
        fillingFunctionArgRef.current = false;
    };

    const onFocusChange = (e) => {
        const currentFormula = e.target.value;
        setAccountsNotFound(checkAccountNumberExistance(currentFormula, allAccounts));
        setFormula(currentFormula);
        setFormulaSelectingRelatedAccount(true);
    };

    const onFocusBlur = (e) => {
        const currentFormula = e.target.value;
        // Capture caret position on blur so a subsequent picker selection can
        // insert at the right spot. The picker steals focus, so onBlur fires
        // before onChange of the Select.
        caretPosRef.current = e.target.selectionStart ?? currentFormula.length;
        setAccountsNotFound(checkAccountNumberExistance(currentFormula, allAccounts));
        setFormula(currentFormula);
        // The account tree stays open while the user browses it; it is closed
        // by a click outside both the tree and the formula field (see below).
    };

    // Close the account tree on a click outside the tree and the formula field.
    useEffect(() => {
        if (!formulaSelectingRelatedAccount) return;

        const onMouseDown = (e) => {
            const inTree = treeRef.current?.contains(e.target);
            const inFormula = formulaWrapperRef.current?.contains(e.target);
            if (!inTree && !inFormula) setFormulaSelectingRelatedAccount(false);
        };

        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [formulaSelectingRelatedAccount]);

    useEffect(() => {
        if (!show) {
            setFormulaSelectingRelatedAccount(false);
            setLastInsertedAccount(null);
            fillingFunctionArgRef.current = false;
        }
    }, [show]);

    const onChangeAddFormulaAllPeriods = (e) => {
        setAddFormulaAllPeriods(e.target.checked);
    };

    // Used by the account tree. The tree stays open so several accounts can be inserted.
    // After a plain account the operator bar is offered; not when the account fills
    // a function argument (the function template already has its own syntax).
    const addRelatedAccountToFormula = (account) => {
        if (!account) return;
        insertAccountToken(account.account_number);

        if (fillingFunctionArgRef.current) {
            fillingFunctionArgRef.current = false;
            setLastInsertedAccount(null);
        } else {
            setLastInsertedAccount(account.account_number);
        }
    };

    const insertOperator = ({ text }) => {
        insertAtCaret(text);
        setLastInsertedAccount(null);
    };

    const getFormulaTextArea = () => formulaInputRef.current?.resizableTextArea?.textArea ?? null;

    // Inserts text at the caret. `caretOffset` is where the caret ends up within
    // the inserted text (defaults to after it).
    const insertAtCaret = (text, caretOffset = text.length) => {
        const el = getFormulaTextArea();
        const livePos = el && document.activeElement === el ? el.selectionStart : null;
        const pos = Math.min(livePos ?? caretPosRef.current ?? formula.length, formula.length);

        const next = formula.slice(0, pos) + text + formula.slice(pos);
        setFormula(next);
        setAccountsNotFound(checkAccountNumberExistance(next, allAccounts));

        const caret = pos + caretOffset;
        caretPosRef.current = caret;
        return caret;
    };

    const insertAccountToken = (accountNumber) => {
        insertAtCaret('{' + accountNumber + '}');
    };

    // Inserts a function template ("|" marks the caret), puts the caret there and
    // opens the account tree so the account argument can be picked straight away.
    const insertFunction = (template) => {
        const caretOffset = Math.max(template.indexOf('|'), 0);
        const caret = insertAtCaret(template.replace('|', ''), caretOffset);

        fillingFunctionArgRef.current = true;
        setLastInsertedAccount(null);
        setFormulaSelectingRelatedAccount(true);
        setTimeout(() => {
            const el = getFormulaTextArea();
            if (!el) return;
            el.focus();
            el.setSelectionRange(caret, caret);
        }, 0);
    };

    const renderFormulaDefinition = () => {
        return (
            <div>
            <Popover
                content={
                    <div ref={treeRef}>
                        <div className="formula-operator-bar">
                            <span>
                                {lastInsertedAccount ? (
                                    <>
                                        Added <code>{`{${lastInsertedAccount}}`}</code> — next:
                                    </>
                                ) : (
                                    'Insert:'
                                )}
                            </span>
                            {OPERATORS.map((operator) => (
                                <Button
                                    key={operator.label}
                                    size="small"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => insertOperator(operator)}
                                >
                                    {operator.label}
                                </Button>
                            ))}
                        </div>
                        <AccountStructureMenu
                            inline
                            handleSelect={addRelatedAccountToFormula}
                            accountStructure={accountStructure}
                        />
                    </div>
                }
                visible={formulaSelectingRelatedAccount}
                trigger={[]}
                placement="bottomLeft"
            >
                <div ref={formulaWrapperRef}>
                    <Input.TextArea
                        ref={formulaInputRef}
                        status={formulaError === '' ? '' : 'error'}
                        placeholder={Localization.accountStructure.formula}
                        value={formula}
                        onChange={onFormulaChange}
                        onFocus={onFocusChange}
                        onBlur={onFocusBlur}
                        autoSize={{ minRows: 8, maxRows: 20 }}
                        style={{ fontFamily: 'monospace', fontSize: '14px' }}
                    />
                </div>
            </Popover>
                <Typography.Text
                    type="secondary"
                    style={{ display: 'block', marginTop: '4px', fontSize: '12px' }}
                >
                    Leave empty to use the entered input instead of a formula.
                </Typography.Text>

                <Typography.Text
                    type="secondary"
                    style={{ display: 'block', marginTop: '2px', fontSize: '12px' }}
                >
                    Click the formula field to search and insert accounts.
                </Typography.Text>

                <FormulaFunctions onInsert={insertFunction} />

                <Typography
                    style={{
                        visibility: accountsNotFound.length === 0 ? 'hidden' : 'visible',
                        minHeight: '22px',
                        marginTop: '10px',
                    }}
                >
                    {'Account(s) ' +
                        accountsNotFound.join(', ') +
                        ' does not exist - add this formula with care.'}
                </Typography>
                <Typography
                    style={{
                        visibility: formulaError === '' ? 'hidden' : 'visible',
                    }}
                >
                    {formulaError}
                </Typography>
                <Space size={'small'}>
                    <Checkbox
                        checked={addFormulaAllPeriods}
                        style={{ marginTop: '10px' }}
                        onChange={onChangeAddFormulaAllPeriods}
                    />
                    <Typography
                        style={{
                            minHeight: '22px',
                            marginTop: '10px',
                        }}
                    >
                        {'Add formula to rest of periods'}
                    </Typography>
                </Space>
            </div>
        );
    };

    return (
        <Modal
            bodyStyle={{
                minHeight: '60vh',
                display: 'flex',
                flexDirection: 'column',
            }}
            title={`Edit formula — ${accountNumber}${accountName ? ' · ' + accountName : ''}`}
            visible={show}
            width={760}
            onOk={handleOk}
            confirmLoading={false}
            onCancel={handleCancel}
        >
            {account !== undefined ? (
                <div>
                    <div style={{ marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid #f0f0f0' }}>
                        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                            Editing formula for
                        </Typography.Text>
                        <Typography.Title level={5} style={{ margin: '2px 0 0 0' }}>
                            <span style={{ fontWeight: 600 }}>{accountNumber}</span>
                            {accountName && (
                                <span style={{ color: '#888', fontWeight: 400, marginLeft: 8 }}>
                                    — {accountName}
                                </span>
                            )}
                        </Typography.Title>
                    </div>
                    {renderFormulaDefinition()}
                </div>
            ) : null}
        </Modal>
    );
};

export default AccountEditing;